/**
 * lazadaOrdersStore — the shapes and pure rules behind LazadaOrdersService.
 *
 * Split from the service for the reason igcomOrdersStore.ts and
 * tiktokOrdersStore.ts were: a test of a JSON packer has no business needing
 * a tenant id.
 *
 * ── A THIRD LIST, NOT A CHANNEL COLUMN ────────────────────────────────────
 * The argument TIKTOK_Orders made holds again and slightly harder. A Lazada
 * order has NO buyer identity of any kind available to us — the app's
 * Sensitive Data Privilege is Mask, so names, phones and streets come back
 * redacted from Lazada itself — and its state is a SET rather than a value,
 * because the status lives on the items. Sharing a list would mean two
 * columns that are always empty for one channel and one that means something
 * different per row, which is the same list twice wearing one name.
 *
 * ── THERE IS NO BUYER KEY AT ALL, AND THAT IS NOT AN OMISSION ─────────────
 * IG COM stores a hashed customer key; TikTok stores one only when it is
 * real. Lazada gives us nothing to store: the order carries no customer id,
 * and the only identifying fields are masked before they reach us. So there
 * is no `buyerKey` field here — not an empty one. An always-empty column
 * invites somebody to fill it later from whatever looks person-shaped, which
 * on this API is a redacted name. The absence is the safer shape.
 *
 * ── BOTH STATUSES ARE KEPT ────────────────────────────────────────────────
 * `status` is the reduced one the table shows; `statuses` is every distinct
 * item state. Keeping only the reduction would make "part shipped" and
 * "shipped" indistinguishable in the store for ever, and the reduction rule
 * is ours rather than Lazada's — a rule we might revise, over data we would
 * then no longer have.
 *
 * Chunked, append-only, newest chunk wins — iSyn2_OrderStatus's shape, for
 * iSyn2_OrderStatus's reasons.
 */

/**
 * On the CRM site, beside IGCOM_Orders, TIKTOK_Orders and B2B_Orders. A plain
 * CRM-style name, not an `iSyn2_` one: the Manufacturing site's Migration
 * Valve does not provision it and must not be asked to.
 */
export const LAZADA_ORDERS_LIST = "LAZADA_Orders";

/**
 * How many orders ride in one row.
 *
 * Matched to IG COM's and TikTok's. A packed Lazada order carries one extra
 * short array (`statuses`) and no SKU list, so it sits comfortably inside the
 * same headroom, and a number that matches its neighbours is one fewer thing
 * to wonder about.
 */
export const ORDERS_PER_ROW = 120;

/** An order's stored facts. No buyer key, no contact detail — see the header. */
export interface ILazadaStoredOrder {
  /** Lazada's order_id as a STRING: it exceeds 2^53 and a JSON number loses
   *  the last digits silently. */
  id: string;
  /** The number a buyer or a CS agent quotes. */
  number: string;
  /** ISO, UTC. Converted from Lazada's "+0800" form at the API boundary. */
  createdAt: string;
  /** The reduced status the table shows. */
  status: string;
  /** Every distinct item state, because the reduction rule is ours. */
  statuses: string[];
  total: number;
  currency: string;
  /** Items on the order, per Lazada's own items_count. */
  quantity: number;
  city: string;
  province: string;
  paymentMethod: string;
}

/** What a caller hands in — the API row, which already carries no contacts. */
export interface ILazadaSyncInput {
  id: string;
  number: string;
  createdAt: string;
  status: string;
  statuses: readonly string[];
  total: number;
  currency: string;
  items: number;
  city: string;
  province: string;
  paymentMethod: string;
}

export interface ILazadaStore {
  orders: ILazadaStoredOrder[];
  /** When the newest chunk was written, ISO, or '' where nothing is stored. */
  syncedAt: string;
  syncedBy: string;
  /** True when the list is not provisioned yet — a state the screen offers to
   *  fix, not an error. */
  missing: boolean;
}

/** One order inside a chunk. Short keys: written thousands of times. */
interface IPacked {
  i: string; n?: string; d: string; s?: string; ss?: string[];
  t: number; c?: string; q: number; y?: string; p?: string; m?: string;
}

const DEFAULT_CURRENCY = "PHP";

/** The one place an API row becomes a stored row, so there is one place to
 *  check that it is safe. */
export function toStoredOrder(o: ILazadaSyncInput): ILazadaStoredOrder {
  return {
    id: String(o.id ?? ""),
    number: String(o.number ?? o.id ?? ""),
    createdAt: o.createdAt || "",
    status: o.status || "",
    statuses: [...new Set((o.statuses ?? []).map((s) => String(s ?? "").trim()).filter(Boolean))],
    total: Number(o.total) || 0,
    currency: o.currency || DEFAULT_CURRENCY,
    quantity: Number(o.items) || 0,
    city: o.city || "",
    province: o.province || "",
    paymentMethod: o.paymentMethod || "",
  };
}

export function pack(o: ILazadaStoredOrder): IPacked {
  const p: IPacked = { i: o.id, d: o.createdAt, t: o.total, q: o.quantity };
  if (o.number && o.number !== o.id) p.n = o.number;
  if (o.status) p.s = o.status;
  // Only when it says more than `status` already does — a single-state order
  // is the common case and its array is pure duplication.
  if (o.statuses.length > 1) p.ss = o.statuses;
  if (o.currency && o.currency !== DEFAULT_CURRENCY) p.c = o.currency;
  if (o.city) p.y = o.city;
  if (o.province) p.p = o.province;
  if (o.paymentMethod) p.m = o.paymentMethod;
  return p;
}

export function unpackChunk(raw: unknown): ILazadaStoredOrder[] {
  let list: unknown;
  try {
    list = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    // A chunk that will not parse is ONE bad row, not a broken list. Losing
    // 120 orders is better than a screen that shows nothing at all and blames
    // the connection.
    return [];
  }
  if (!Array.isArray(list)) return [];

  return list.map((r) => {
    const p = (r ?? {}) as IPacked;
    const status = String(p.s ?? "");
    return {
      id: String(p.i ?? ""),
      number: String(p.n ?? p.i ?? ""),
      createdAt: String(p.d ?? ""),
      status,
      statuses: Array.isArray(p.ss) ? p.ss.map(String) : (status ? [status] : []),
      total: Number(p.t) || 0,
      currency: String(p.c ?? DEFAULT_CURRENCY),
      quantity: Number(p.q) || 0,
      city: String(p.y ?? ""),
      province: String(p.p ?? ""),
      paymentMethod: String(p.m ?? ""),
    };
  }).filter((o) => o.id);
}

/** Everything a sync could have changed, as one string. */
function fingerprint(o: ILazadaStoredOrder): string {
  return [
    o.createdAt, o.status, o.statuses.join("|"), o.total, o.currency,
    o.quantity, o.city, o.province, o.paymentMethod,
  ].join("\u0001");
}

/**
 * Which of `live` the list does not already agree with.
 *
 * Lazada earns this the way TikTok does: an order's item states walk from
 * pending to delivered over days, so a window re-synced tomorrow genuinely HAS
 * changed and must write — while the months behind it have not and must not.
 */
export function changedOrders(
  live: readonly ILazadaStoredOrder[],
  stored: readonly ILazadaStoredOrder[],
): ILazadaStoredOrder[] {
  const have = new Map(stored.map((o) => [o.id, fingerprint(o)]));
  return live.filter((o) => have.get(o.id) !== fingerprint(o));
}

export interface IBackfillPoint {
  /** ISO instant to pass as `before`. */
  cursor: string;
  /** What to show on the button. */
  from: string;
  reason: "gap" | "oldest";
  gapDays: number;
}

const MIN_GAP_MS = 7 * 86_400_000;
const GAP_RATIO = 20;

/**
 * Where a backfill should resume, or null when the history looks complete.
 *
 * ── THE STORE IS THE CURSOR ───────────────────────────────────────────────
 * Not a saved "the walk reached March" row, for the reason set out at length
 * in tiktokOrdersStore: a saved cursor is a second source of truth about the
 * same fact, and it can be written when the write that earned it failed. The
 * oldest order actually IN the store cannot drift, because it is not a claim
 * about the store — it is the store.
 *
 * ── IT LOOKS FOR HOLES, NOT JUST FOR THE END ──────────────────────────────
 * Taking the single oldest order would declare "history complete" over a
 * multi-year hole left by an interrupted or mis-ordered walk. So the gaps
 * between consecutive orders are measured against the MEDIAN step — a mean
 * would be dragged past every real gap by the one big hole it is looking for
 * — and the largest disproportionate gap wins.
 */
export function backfillCursor(
  stored: readonly ILazadaStoredOrder[],
  opts: { ignoreGaps?: boolean } = {},
): IBackfillPoint | null {
  const times = stored
    .map((o) => Date.parse(o.createdAt))
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => b - a);
  if (times.length === 0) return null;

  const label = (t: number): string =>
    new Date(t).toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" });

  if (!opts.ignoreGaps && times.length > 3) {
    const steps: number[] = [];
    for (let i = 1; i < times.length; i += 1) steps.push(times[i - 1] - times[i]);
    const sorted = [...steps].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] || 0;

    let worst = -1;
    let worstAt = 0;
    for (let i = 1; i < times.length; i += 1) {
      const gap = times[i - 1] - times[i];
      if (gap < MIN_GAP_MS) continue;
      if (median > 0 && gap < median * GAP_RATIO) continue;
      if (gap > worst) { worst = gap; worstAt = times[i - 1]; }
    }
    if (worst > 0) {
      return {
        cursor: new Date(worstAt).toISOString(),
        from: label(worstAt),
        reason: "gap",
        gapDays: Math.round(worst / 86_400_000),
      };
    }
  }

  const oldest = times[times.length - 1];
  return {
    // `created_before` is strictly less than, so the oldest order we hold is
    // excluded from the next page rather than re-fetched. That is the correct
    // direction here: unlike TikTok's per-second bursts, Lazada's timestamps
    // are per-second on a store that does not take several orders a second,
    // and a re-fetch would cost a page to learn nothing.
    cursor: new Date(oldest).toISOString(),
    from: label(oldest),
    reason: "oldest",
    gapDays: 0,
  };
}
